import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Loader2, AlertCircle, CheckCircle, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getAppUrl } from '../../lib/url';
import { isValidEmail, readFunctionError } from '../../lib/email';
import { errorMessage } from '../../lib/errors';


interface EmailInviteModalProps {
    isOpen: boolean;
    onClose: () => void;
    inviteeEmail: string;
    inviteeName: string;
    inviteToken: string;
    orgId: string;
    orgName: string;
    inviterName: string;
    onSuccess: () => void;
}

export default function EmailInviteModal({
    isOpen,
    onClose,
    inviteeEmail,
    inviteeName,
    inviteToken,
    orgId,
    orgName,
    inviterName,
    onSuccess,
}: EmailInviteModalProps) {
    const [emailTo, setEmailTo] = useState(inviteeEmail);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');
    const [error, setError] = useState<string | null>(null);

    // Reset/Populate form when opening
    useEffect(() => {
        if (isOpen) {
            setEmailTo(inviteeEmail);
            setSubject(`You're invited to join ${orgName} on ForgeDash`);
            setMessage(
                `Hey ${inviteeName},\n\n` +
                `${inviterName} is inviting you to join ${orgName} on ForgeDash — the workshop management platform that keeps your team, jobs, and invoices all in one place.\n\n` +
                `Click the link below to accept the invitation and get started. It takes less than a minute to set up your account.\n\n` +
                `We can't wait to have you on the team!\n\n` +
                `Cheers,\n${inviterName}`
            );
            setStatus('idle');
            setError(null);
        }
    }, [isOpen, inviteeEmail, inviteeName, orgName, inviterName]);

    const handleClose = () => {
        if (status === 'sending') return;
        setError(null);
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!emailTo.trim()) {
            setError('Recipient email is required');
            return;
        }
        if (!isValidEmail(emailTo)) {
            setError('That email address looks invalid — check it includes a domain like "name@example.com".');
            return;
        }

        try {
            setStatus('sending');

            const inviteLink = `${getAppUrl()}/?invite_token=${inviteToken}`;

            const { error: fnError } = await supabase.functions.invoke('send-email', {
                body: {
                    type: 'team_invite',
                    data: {
                        // org_id is required by send-email to authorise the caller
                        // as a member of this org — without it the send is 401'd.
                        org_id: orgId,
                        email: emailTo.trim(),
                        invitee_name: inviteeName,
                        inviter_name: inviterName,
                        org_name: orgName,
                        invite_link: inviteLink,
                        app_url: getAppUrl(),
                        subject: subject.trim(),
                        message: message.trim(),
                    }
                }
            });

            if (fnError) throw await readFunctionError(fnError, 'Failed to send invitation email');

            setStatus('success');

            setTimeout(() => {
                onSuccess();
            }, 1500);
        } catch (err) {
            console.error('Invite email error:', err);
            setError(errorMessage(err) || 'Failed to send invitation email');
            setStatus('idle');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-tint/10">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-accent" />
                        Send Team Invitation
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg"
                        disabled={status === 'sending'}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* To Field — bound to the invite, not editable.
                        The invite token was issued for THIS address and the
                        send-email allowlist authorises the recipient against the
                        pending invite row, so an edited address is refused. */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            To
                        </label>
                        <input
                            type="email"
                            readOnly
                            value={emailTo}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg-2 outline-none cursor-not-allowed"
                        />
                        <p className="mt-1 text-xs text-fg-4">
                            The invite link is issued for this address. To invite someone else, revoke this
                            invite and create a new one.
                        </p>
                    </div>

                    {/* Subject Field */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            Subject <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none disabled:opacity-50"
                        />
                    </div>

                    {/* Message body */}
                    <div>
                        <label className="block text-xs font-bold text-fg-4 uppercase mb-1">
                            Message
                        </label>
                        <textarea
                            rows={7}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={status !== 'idle'}
                            className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-2.5 text-fg focus:ring-2 focus:ring-accent outline-none resize-none disabled:opacity-50"
                        />
                    </div>

                    {/* Invite link preview */}
                    <div className="flex items-center gap-3 p-3 border border-tint/10 rounded-lg bg-tint/5">
                        <div className="w-10 h-10 bg-accent/20 rounded flex items-center justify-center">
                            <Send className="w-4 h-4 text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-fg truncate">Invitation Link</p>
                            <p className="text-xs text-fg-4">
                                A branded "Accept Invitation" button will be included in the email
                            </p>
                        </div>
                        <CheckCircle className="w-5 h-5 text-success" />
                    </div>

                    {/* Error Display */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm"
                            >
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Submit Button */}
                    <div className="pt-4 flex gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="flex-1 px-4 py-2.5 rounded-lg font-bold text-fg-2 hover:bg-tint/5 border border-tint/10 transition-colors disabled:opacity-50"
                            disabled={status === 'sending'}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={status !== 'idle'}
                            className={`flex-1 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 min-w-[120px] ${status === 'success'
                                ? 'bg-success-fill text-on-fill'
                                : 'fd-btn-primary'
                                }`}
                        >
                            {status === 'idle' && (
                                <>
                                    <Send className="w-5 h-5" />
                                    Send Invitation
                                </>
                            )}
                            {status === 'sending' && (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Sending...
                                </>
                            )}
                            {status === 'success' && (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    Sent!
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}
